import { useState } from 'react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
  LabelList,
} from 'recharts'
import { Eye, MousePointerClick, TrendingUp, ArrowUp, Radio, Calendar } from 'lucide-react'

// ── Datos ──────────────────────────────────────────────────────────────────

const PROGRAMMATIC = [
  { mes: 'Mes 1', label: 'Marzo', impresiones: 1026705, clicks: 3599, ctr: 0.35 },
  { mes: 'Mes 2', label: 'Mayo',  impresiones: 524789,  clicks: 3388, ctr: 0.65 },
  { mes: 'Mes 3', label: 'Junio', impresiones: 558220,  clicks: 3367, ctr: 0.60 },
]

const GOOGLE_ADS = [
  { mes: 'Mes 1', label: 'Julio', impresiones: 40397, clicks: 5237, ctr: 12.96 },
]

type Canal = 'programmatic' | 'ads' | 'ambos'

// ── Helpers ────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return new Intl.NumberFormat('es-EC').format(n)
}

function pct(n: number) {
  return `${n.toFixed(2)}%`
}

function fmtShort(n: number) {
  if (n >= 1000000) return `${parseFloat((n / 1000000).toFixed(2))}M`
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(1))}k`
  return String(n)
}

// ── Componentes locales ────────────────────────────────────────────────────

function KpiCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType
  label: string
  value: string
  color: string
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 flex items-center gap-4 border border-gray-100">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </div>
  )
}

// ── Página ─────────────────────────────────────────────────────────────────

export function FerrerCampanas2025Page() {
  const [canal, setCanal] = useState<Canal>('ambos')

  const progVisible = canal === 'programmatic' || canal === 'ambos'
  const adsVisible  = canal === 'ads'          || canal === 'ambos'

  // Totales según filtro
  const filas = [
    ...(progVisible ? PROGRAMMATIC.map(d => ({ ...d, canal: 'Programmatic' })) : []),
    ...(adsVisible  ? GOOGLE_ADS.map(d => ({ ...d, canal: 'Google Ads' }))     : []),
  ]
  const totalImpresiones = filas.reduce((s, r) => s + r.impresiones, 0)
  const totalClicks      = filas.reduce((s, r) => s + r.clicks, 0)
  const ctrPromedio      = totalImpresiones > 0 ? (totalClicks / totalImpresiones) * 100 : 0

  // Datos para la línea chart: Mes 1, Mes 2, Mes 3
  const meses = ['Mes 1', 'Mes 2', 'Mes 3']
  const lineData = meses.map(mes => {
    const prog = PROGRAMMATIC.find(d => d.mes === mes)
    const ads  = GOOGLE_ADS.find(d => d.mes === mes)
    return {
      mes,
      prog_clicks:  progVisible && prog ? prog.clicks      : undefined,
      prog_ctr:     progVisible && prog ? prog.ctr         : undefined,
      prog_imp:     progVisible && prog ? prog.impresiones : undefined,
      ads_clicks:   adsVisible  && ads  ? ads.clicks       : undefined,
      ads_ctr:      adsVisible  && ads  ? ads.ctr          : undefined,
      ads_imp:      adsVisible  && ads  ? ads.impresiones  : undefined,
    }
  })

  const TABS: { value: Canal; label: string }[] = [
    { value: 'ambos',         label: 'Ambos' },
    { value: 'programmatic',  label: 'Programmatic' },
    { value: 'ads',           label: 'Google Ads' },
  ]

  return (
    <div className="bg-gray-100 min-h-screen p-4 lg:p-6">
      <div className="max-w-5xl mx-auto space-y-6">

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900">Ferrer — Campañas Digitales 2026</h1>
              <p className="text-sm text-gray-500 mt-0.5">Programmatic (Mar–Jun) · Google Ads (Jul en curso)</p>
            </div>
            <img src="/logo-tonal-negro.png" alt="Tonal" className="h-7 w-auto opacity-70" />
          </div>
        </div>

        {/* Filtro */}
        <div className="flex gap-2">
          {TABS.map(t => (
            <button
              key={t.value}
              onClick={() => setCanal(t.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
                canal === t.value
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-4">
          <KpiCard icon={Eye}              label="Impresiones"  value={fmt(totalImpresiones)} color="bg-blue-500" />
          <KpiCard icon={MousePointerClick} label="Clicks"      value={fmt(totalClicks)}       color="bg-emerald-500" />
          <KpiCard icon={TrendingUp}        label="CTR promedio" value={pct(ctrPromedio)}      color="bg-violet-500" />
        </div>

        {/* Gráfica de Impresiones */}
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">Impresiones por mes</h2>
            <Eye className="h-4 w-4 text-gray-400" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={lineData} margin={{ top: 20, right: 16, bottom: 0, left: 0 }} barCategoryGap="30%" barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} width={65} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip formatter={(v: number) => fmt(v)} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {progVisible && (
                <Bar dataKey="prog_imp" name="Programmatic" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                  {lineData.map((_, i) => <Cell key={i} fillOpacity={lineData[i].prog_imp == null ? 0 : 1} />)}
                  <LabelList dataKey="prog_imp" position="top" fontSize={11} fill="#3b82f6" formatter={(v: number) => v != null ? fmtShort(v) : ''} />
                </Bar>
              )}
              {adsVisible && (
                <Bar dataKey="ads_imp" name="Google Ads" fill="#10b981" radius={[4, 4, 0, 0]}>
                  {lineData.map((_, i) => <Cell key={i} fillOpacity={lineData[i].ads_imp == null ? 0 : 1} />)}
                  <LabelList dataKey="ads_imp" position="top" fontSize={11} fill="#10b981" formatter={(v: number) => v != null ? fmtShort(v) : ''} />
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfica de Clicks */}
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">Clicks por mes</h2>
            <MousePointerClick className="h-4 w-4 text-gray-400" />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={lineData} margin={{ top: 20, right: 16, bottom: 0, left: 0 }} barCategoryGap="30%" barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} width={50} />
              <Tooltip formatter={(v: number) => fmt(v)} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {progVisible && (
                <Bar dataKey="prog_clicks" name="Programmatic" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                  {lineData.map((_, i) => <Cell key={i} fillOpacity={lineData[i].prog_clicks == null ? 0 : 1} />)}
                  <LabelList dataKey="prog_clicks" position="top" fontSize={11} fill="#3b82f6" formatter={(v: number) => v != null ? fmtShort(v) : ''} />
                </Bar>
              )}
              {adsVisible && (
                <Bar dataKey="ads_clicks" name="Google Ads" fill="#10b981" radius={[4, 4, 0, 0]}>
                  {lineData.map((_, i) => <Cell key={i} fillOpacity={lineData[i].ads_clicks == null ? 0 : 1} />)}
                  <LabelList dataKey="ads_clicks" position="top" fontSize={11} fill="#10b981" formatter={(v: number) => v != null ? fmtShort(v) : ''} />
                </Bar>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Gráfica de CTR */}
        <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-gray-700">CTR % por mes</h2>
            <div className="flex items-center gap-3">
              {canal === 'programmatic' && (
                <span className="text-xs text-gray-400 flex items-center gap-1.5">
                  <span className="inline-block w-6 border-t-2 border-dashed border-orange-400" />
                  Promedio industria programmatic 0.20%
                </span>
              )}
              {canal === 'ads' && (
                <span className="text-xs text-gray-400 flex items-center gap-1.5">
                  <span className="inline-block w-6 border-t-2 border-dashed border-violet-400" />
                  Rango industria Google Ads 3–5%
                </span>
              )}
              <TrendingUp className="h-4 w-4 text-gray-400" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={lineData} margin={{ top: 20, right: 16, bottom: 0, left: 0 }} barCategoryGap="30%" barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} width={45} unit="%" />
              <Tooltip formatter={(v: number) => pct(v)} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              {progVisible && (
                <Bar dataKey="prog_ctr" name="Programmatic" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                  {lineData.map((_, i) => <Cell key={i} fillOpacity={lineData[i].prog_ctr == null ? 0 : 1} />)}
                  <LabelList dataKey="prog_ctr" position="top" fontSize={11} fill="#3b82f6" formatter={(v: number) => v != null ? `${v}%` : ''} />
                </Bar>
              )}
              {adsVisible && (
                <Bar dataKey="ads_ctr" name="Google Ads" fill="#10b981" radius={[4, 4, 0, 0]}>
                  {lineData.map((_, i) => <Cell key={i} fillOpacity={lineData[i].ads_ctr == null ? 0 : 1} />)}
                  <LabelList dataKey="ads_ctr" position="top" fontSize={11} fill="#10b981" formatter={(v: number) => v != null ? `${v}%` : ''} />
                </Bar>
              )}
              {canal === 'programmatic' && (
                <ReferenceLine
                  y={0.20}
                  stroke="#fb923c"
                  strokeDasharray="4 3"
                  strokeWidth={2}
                  label={{ value: '0.20%', position: 'insideTopRight', fontSize: 10, fill: '#fb923c' }}
                />
              )}
              {canal === 'ads' && (
                <ReferenceLine
                  y={4}
                  stroke="#a78bfa"
                  strokeDasharray="4 3"
                  strokeWidth={2}
                  label={{ value: '3-5%', position: 'insideTopRight', fontSize: 10, fill: '#a78bfa' }}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <h2 className="text-sm font-semibold text-gray-700">Detalle por mes</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left px-5 py-3"><span className="inline-flex items-center gap-1.5"><Radio className="h-3.5 w-3.5" />Canal</span></th>
                  <th className="text-left px-4 py-3"><span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />Mes</span></th>
                  <th className="text-right px-4 py-3"><span className="inline-flex items-center justify-end gap-1.5"><Eye className="h-3.5 w-3.5" />Impresiones</span></th>
                  <th className="text-right px-4 py-3"><span className="inline-flex items-center justify-end gap-1.5"><MousePointerClick className="h-3.5 w-3.5" />Clicks</span></th>
                  <th className="text-right px-4 py-3"><span className="inline-flex items-center justify-end gap-1.5"><TrendingUp className="h-3.5 w-3.5" />CTR</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {progVisible && PROGRAMMATIC.map(d => (
                  <tr key={`prog-${d.mes}`} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full">
                        Programmatic
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{d.mes} · {d.label}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(d.impresiones)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(d.clicks)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className="inline-flex items-center justify-end gap-1">
                        {pct(d.ctr)}
                        {d.ctr > 0.20 && <ArrowUp className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                      </span>
                    </td>
                  </tr>
                ))}
                {adsVisible && GOOGLE_ADS.map(d => (
                  <tr key={`ads-${d.mes}`} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                        Google Ads
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{d.mes} · {d.label}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(d.impresiones)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{fmt(d.clicks)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      <span className="inline-flex items-center justify-end gap-1">
                        {pct(d.ctr)}
                        {d.ctr > 3 && <ArrowUp className="h-3.5 w-3.5 text-emerald-500 shrink-0" />}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 pb-2">
          Tonal Strategies · Datos Google Ads julio 2025 preliminares
        </p>
      </div>
    </div>
  )
}
